import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';

import BatchInvoiceDataRow from './BatchInvoiceDataRow';
import BatchInvoiceHeaderTitle from './BatchInvoiceHeaderTitle';
import BatchInvoiceMixedPaymentListTable from './BatchInvoiceMixedPaymentListTable';
import BatchInvoicePageMessage from './BatchInvoicePageMessage';
import BatchInvoiceTableHeaderRow from './BatchInvoiceTableHeaderRow';
import { BATCH_PAYMENT_INVOICE_SELECTION } from './constants';
import {
  sortInvoicesWithDefaultFirst,
  isInvoiceSelectable,
  getMaxSelectableInvoiceBalance,
  shouldDisableEditAmount,
  buildPaymentMethods,
  buildCurrentPageLink,
  createInvoiceIdsKey,
  calculateTotalAmount,
  getAllowedBatchPaymentMethods,
  getDefaultSelectedInvoices,
} from './helpers';

import InvoiceViewer from 'components/Core/Invoice/Viewer/InvoiceViewer';
import SegmentIO from 'reporting/SegmentIO.js';
import { appendLinkSourceParam } from 'reporting/helpers';
import { insightSelectors } from 'store/insight/selectors';
import { insightActions } from 'store/insight/slice';
import { fetchPDFDocument, downloadPDFDocument } from 'store/invoiceDocument/slice';
import { modalActions } from 'store/modal/slice';
import { paymentAmountChange, paymentMethodTypeChange, paymentActions } from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { saleActions } from 'store/sale/slice';
import { walletSelectors } from 'store/wallet/selectors';
import { colors, fontSize } from 'styles/cp';
import { Insight, UnpaidInvoice } from 'types/Insight';
import { Sale } from 'types/Sale';

interface BatchInvoiceListTableProps {
  unpaidInvoices: {
    unpaidInvoices: UnpaidInvoice[];
    originalInvoicesCount?: number; // Original count before limiting to 100
  } | null;
  currency: string;
  fetchPDFDocument: (token: string) => void;
  insight: Insight;
  sale: Sale;
  showModal: (modalConfig: {
    component: React.ComponentType<any>;
    componentProps: Record<string, any>;
  }) => void;
  invoiceDocument: any;
  region?: string;
  featureFlags?: Record<string, boolean>;
  amountConfig?: {
    maxAllowedAmount: number;
    maxAllowedAmountCcDcACH: number;
    minPaymentAmount: number;
    multipleFactor: number;
  };
}

const BatchInvoiceListTable = ({
  unpaidInvoices: unpaidInvoicesData,
  currency,
  enabledPaymentMethods,
  fetchPDFDocument,
  insight,
  sale,
  showModal,
  invoiceDocument,
  region,
  featureFlags,
  amountConfig,
}: BatchInvoiceListTableProps & {
  enabledPaymentMethods: string[];
}) => {
  const dispatch = useDispatch();
  const token = insightSelectors.tokenSelector(insight);

  // Get payment input amount from Redux to detect partial payments
  const paymentInputAmount = useSelector((state: any) => state.payment.inputAmount);

  const { fetchPDFStatus } = invoiceDocument || {};
  const notFetched = fetchPDFStatus === null;

  const showDefaultPaymentMethodTable = false;

  // Calculate the maximum selectable invoice balance based on merchant config
  const maxSelectableBalance = useMemo(
    () => getMaxSelectableInvoiceBalance(region, featureFlags, amountConfig),
    [region, featureFlags, amountConfig]
  );

  // Extract the unpaidInvoices array from the response object and filter out non-regular invoices
  // Exclude: scheduled payments, tips, forced autopay, subscriptions
  const unpaidInvoicesArray = (unpaidInvoicesData?.unpaidInvoices || []).filter(
    (invoice) => !invoice.scheduled // Scheduled payments are not allowed in batch payments
  );

  // Initialize with unpaidInvoices data to ensure it renders
  const [batchInvoices, setBatchInvoices] = useState<UnpaidInvoice[]>(() => {
    if (!unpaidInvoicesArray || unpaidInvoicesArray.length === 0) return [];
    const sortedInvoices = sortInvoicesWithDefaultFirst(unpaidInvoicesArray);

    // Categorize invoices by payment method for treatment logic
    const categorizedInvoices = saleSelectors.categorizeInvoicesByPaymentMethod(sortedInvoices);

    // Always select all selectable invoices in the table
    const defaultSelectedIds = getDefaultSelectedInvoices(
      sortedInvoices,
      BATCH_PAYMENT_INVOICE_SELECTION.ALL_IN_SAME_TABLE,
      categorizedInvoices,
      maxSelectableBalance
    );

    return sortedInvoices.map((invoice) => ({
      ...invoice,
      selected: defaultSelectedIds.includes(invoice.saleId),
    }));
  });

  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [tooltipStates, setTooltipStates] = useState<Record<string, boolean>>({});

  // Track the invoice IDs to detect when actual data changes (not just re-renders)
  const prevInvoiceIdsRef = useRef<string>('');
  const hasTrackedTableView = useRef<boolean>(false);

  // Update batch invoices only when the actual data changes (not just re-renders)
  useEffect(() => {
    // Use the already filtered unpaidInvoicesArray (excludes scheduled invoices)
    if (unpaidInvoicesArray.length === 0) return;

    const currentInvoiceIds = createInvoiceIdsKey(unpaidInvoicesArray);

    // Only update if the invoices actually changed (not just re-rendered)
    if (currentInvoiceIds !== prevInvoiceIdsRef.current) {
      const sortedInvoices = sortInvoicesWithDefaultFirst(unpaidInvoicesArray);

      // Categorize invoices by payment method for treatment logic
      const categorizedInvoices = saleSelectors.categorizeInvoicesByPaymentMethod(sortedInvoices);

      // Always select all selectable invoices in the table
      const defaultSelectedIds = getDefaultSelectedInvoices(
        sortedInvoices,
        BATCH_PAYMENT_INVOICE_SELECTION.ALL_IN_SAME_TABLE,
        categorizedInvoices,
        maxSelectableBalance
      );

      setBatchInvoices(
        sortedInvoices.map((invoice) => ({
          ...invoice,
          selected: defaultSelectedIds.includes(invoice.saleId),
        }))
      );
      prevInvoiceIdsRef.current = currentInvoiceIds;
    }
  }, [unpaidInvoicesData]);

  const customerId = saleSelectors.customerLocalNameIdSelector(sale) || undefined;

  // Track batch payments table viewed event
  useEffect(() => {
    if (batchInvoices.length > 0 && !hasTrackedTableView.current) {
      // Check if URL has source=batchPayment parameter
      const urlParams = new URLSearchParams(window.location.search);
      const isSourceBatchPayment = urlParams.get('source') === 'batchPayment';

      // @ts-ignore
      SegmentIO.batchPaymentsTableViewed({
        invoiceList: batchInvoices,
        totalInvoicesCount: batchInvoices.length,
        isSourceBatchPayment,
        customer_id: customerId,
      });

      hasTrackedTableView.current = true;
    }
  }, [batchInvoices, customerId]);

  const INVOICE_DISPLAY_THRESHOLD = 5;

  const displayedInvoices = useMemo(() => {
    return showAllInvoices ? batchInvoices : batchInvoices.slice(0, INVOICE_DISPLAY_THRESHOLD);
  }, [showAllInvoices, batchInvoices]);

  //show toggle button only if there are more than 5 invoices
  const shouldShowToggleButton = useMemo(() => {
    return batchInvoices.length > INVOICE_DISPLAY_THRESHOLD;
  }, [batchInvoices.length]);

  // Use originalInvoicesCount if available (to show 100+ when there are more than 100)
  // Otherwise use the actual batchInvoices length
  const invoicesCount = unpaidInvoicesData?.originalInvoicesCount || batchInvoices.length;

  // Check if default invoice exceeds the max amount
  const isDefaultInvoiceOverLimit = useMemo(() => {
    const defaultInvoice = batchInvoices.find((invoice) => invoice.defaultInvoice);
    return defaultInvoice ? defaultInvoice.balance > maxSelectableBalance : false;
  }, [batchInvoices, maxSelectableBalance]);

  // Calculate partial payment amount for default invoice (if applicable)
  const partialPaymentAmount = useMemo(() => {
    const defaultInvoice = batchInvoices.find((invoice) => invoice.defaultInvoice);
    if (!defaultInvoice) return null;

    const selectedInvoices = batchInvoices.filter((invoice) => invoice.selected);

    // Only show partial if only default invoice is selected
    if (selectedInvoices.length !== 1 || !selectedInvoices[0].defaultInvoice) {
      return null;
    }

    const inputAmount = Number(paymentInputAmount || 0);
    const originalBalance = defaultInvoice.balance;

    // Only show partial if amount was edited and is less than the original balance
    if (inputAmount > 0 && inputAmount < originalBalance) {
      return inputAmount;
    }

    return null;
  }, [batchInvoices, paymentInputAmount]);

  // Calculate payment methods order based on currently selected invoices
  const paymentMethodsOrder = useMemo(() => {
    const selectedInvoices = batchInvoices.filter((invoice) => invoice.selected);

    if (selectedInvoices.length === 0 || !enabledPaymentMethods) return [];

    const allowedMethods = getAllowedBatchPaymentMethods(
      selectedInvoices,
      enabledPaymentMethods,
      region
    );

    const { getPaymentMethodsOrder } = require('shared/clientUtils');
    const orderedMethods = getPaymentMethodsOrder({
      enabledPaymentMethods: allowedMethods,
    });

    return orderedMethods;
  }, [batchInvoices, enabledPaymentMethods, region]);

  // Check if all selectable invoices are selected
  const isAllChecked = useMemo(() => {
    const selectableInvoices = batchInvoices.filter((invoice) =>
      isInvoiceSelectable(invoice, maxSelectableBalance)
    );
    return !selectableInvoices.some((invoice) => !invoice.selected);
  }, [batchInvoices, maxSelectableBalance]);

  // Determine if edit amount should be disabled (only enabled when ONLY default invoice selected)
  const isEditAmountDisabled = useMemo(() => {
    const selectedInvoices = batchInvoices.filter((invoice) => invoice.selected);
    return shouldDisableEditAmount(selectedInvoices);
  }, [batchInvoices]);

  const { overDueTotalAmount, dueTotalAmount } = useMemo(() => {
    return batchInvoices.reduce(
      (acc, invoice) => {
        if (invoice.overDue) {
          acc.overDueTotalAmount += invoice.balance;
        } else {
          acc.dueTotalAmount += invoice.balance;
        }
        return acc;
      },
      { overDueTotalAmount: 0, dueTotalAmount: 0 }
    );
  }, [batchInvoices]);

  useEffect(() => {
    dispatch(saleActions.setIsEditAmountDisabled(isEditAmountDisabled));
  }, [dispatch, isEditAmountDisabled]);

  // Update Redux store whenever batchInvoices state changes (for PreviewCard summary)
  useEffect(() => {
    if (batchInvoices.length === 0) return;

    const selectedInvoices = batchInvoices.filter((invoice) => invoice.selected);
    dispatch(insightActions.updateSelectedBatchInvoices(selectedInvoices));

    // Calculate total with capped amounts for payment
    const totalAmount = calculateTotalAmount(batchInvoices, maxSelectableBalance);
    // @ts-ignore - paymentAmountChange expects a number
    dispatch(paymentAmountChange(totalAmount));

    // Reset scheduled date if multiple invoices selected or non-default invoice selected
    // Only keep scheduled date when ONLY the default invoice is selected
    const onlyDefaultSelected =
      selectedInvoices.length === 1 && selectedInvoices[0]?.defaultInvoice;

    if (!onlyDefaultSelected) {
      // Reset scheduled date to today and isDateScheduled to false
      dispatch(
        paymentActions.setScheduledDate({
          scheduledDate: new Date(Date.now()),
          isDateScheduled: false,
        })
      );
    }
  }, [batchInvoices, dispatch, maxSelectableBalance]);

  // Get current payment method from Redux state
  const currentPaymentMethod = useSelector((state: any) => state.payment.paymentMethodType);

  /**
   * Auto-select appropriate payment method when available methods change.
   * This handles two scenarios:
   * 1. When only one payment method is available - auto-select it
   * 2. When switching between invoice types (e.g., ACH-only to CC-only) -
   *    if current method is no longer available, select the first available method
   * 3. When all invoices are deselected, fall back to basic payment methods
   */
  useEffect(() => {
    if (!enabledPaymentMethods) return;

    // When no invoices selected, use basic payment methods (same logic as PaymentForm.mapStateToProps)
    let methodsToCheck = paymentMethodsOrder;
    if (paymentMethodsOrder.length === 0) {
      const basicPaymentMethods = enabledPaymentMethods.filter(
        (method) => !['paypal', 'paypal_ppaam', 'venmo', 'pp', 'ap'].includes(method)
      );
      const { getPaymentMethodsOrder } = require('shared/clientUtils');
      methodsToCheck = getPaymentMethodsOrder({
        enabledPaymentMethods: basicPaymentMethods,
      });
    }

    if (methodsToCheck.length === 0) return;

    const isCurrentMethodAvailable = methodsToCheck.includes(currentPaymentMethod);

    if (!isCurrentMethodAvailable) {
      const targetMethod = methodsToCheck[0];

      (dispatch as any)(
        (paymentMethodTypeChange as any)({
          paymentMethodType: targetMethod,
        })
      );
    }
  }, [paymentMethodsOrder, currentPaymentMethod, enabledPaymentMethods, dispatch]);

  const handleSelectAllCheck = useCallback(() => {
    setBatchInvoices((prev) =>
      prev?.map((invoice) => {
        // If unchecking all, uncheck all invoices (including default)
        if (isAllChecked) {
          return { ...invoice, selected: false };
        }
        // If checking all
        if (!isAllChecked) {
          // If default invoice is over limit, don't select any non-default invoices
          if (isDefaultInvoiceOverLimit && !invoice.defaultInvoice) {
            return invoice;
          }
          // Otherwise, only check selectable invoices
          return {
            ...invoice,
            selected: invoice.defaultInvoice || isInvoiceSelectable(invoice, maxSelectableBalance),
          };
        }
        return invoice;
      })
    );
  }, [isAllChecked, maxSelectableBalance, isDefaultInvoiceOverLimit]);

  const handleCheckboxChange = useCallback((targetInvoice: UnpaidInvoice) => {
    // Track if this is deselecting the default invoice
    const wasDefaultInvoiceDeselected =
      targetInvoice.defaultInvoice && targetInvoice.selected === true;

    setBatchInvoices((prev) => {
      const updated = prev.map((invoice) =>
        // Use saleId as unique identifier (always present and unique)
        invoice.saleId === targetInvoice.saleId
          ? { ...invoice, selected: !invoice.selected }
          : invoice
      );

      // Track analytics after state update
      const selectedInvoices = updated.filter((inv) => inv.selected);

      // Track invoice selection event - send full invoice objects
      // @ts-ignore
      SegmentIO.batchPaymentsInvoiceSelected({
        totalInvoicesSelected: selectedInvoices.length,
        selectedInvoicesList: selectedInvoices,
        customer_id: customerId,
      });

      // Track default invoice deselection specifically
      if (wasDefaultInvoiceDeselected) {
        // @ts-ignore
        SegmentIO.batchPaymentsDefaultInvoiceDeselected({
          defaultInvoiceNumber: targetInvoice.docNumber || '',
          remainingSelectedCount: selectedInvoices.length,
          customer_id: customerId,
        });
      }

      return updated;
    });
  }, []);

  const handleShowToolTip = useCallback((docNumber: string, show: boolean) => {
    setTooltipStates((prev) => ({
      ...prev,
      [docNumber]: show,
    }));
  }, []);

  const handleToggleShowAll = useCallback(() => {
    setShowAllInvoices((prev) => !prev);
  }, []);

  const handleNewWindowClick = useCallback((link: string) => {
    // Add cta=batchpayment for Amplitude link_source and source=batchPayment for internal tracking
    const url = new URL(appendLinkSourceParam(link, 'batchpayment'));
    url.searchParams.set('source', 'batchPayment');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }, []);

  const handleDocumentClick = useCallback(() => {
    if (notFetched) {
      fetchPDFDocument(token);
    }
    showModal({
      component: InvoiceViewer,
      componentProps: {
        closeOnClickOutSide: true,
        closeOnEsc: false,
        showCloseButton: true,
      },
    });
  }, [fetchPDFDocument, token, notFetched, showModal]);

  // Don't render anything if no invoices - but do it consistently
  if (!displayedInvoices || displayedInvoices.length === 0) {
    return null;
  }

  return (
    <>
      <div className="batch-invoice-list-table">
        <BatchInvoiceHeaderTitle
          invoicesCount={invoicesCount}
          dueTotalAmount={dueTotalAmount}
          overDueTotalAmount={overDueTotalAmount}
          currency={currency}
        />
        <BatchInvoicePageMessage
          batchInvoices={batchInvoices}
          maxSelectableBalance={maxSelectableBalance}
          currency={currency}
        />

        {showDefaultPaymentMethodTable ? (
          <div className="invoice-table-container">
            {/* Header Row */}
            <BatchInvoiceTableHeaderRow
              isAllChecked={isAllChecked}
              handleSelectAllCheck={handleSelectAllCheck}
            />

            {/* Data Rows */}
            {displayedInvoices?.map((invoice: any, index: number) => (
              <BatchInvoiceDataRow
                key={invoice.saleId || index}
                invoice={invoice}
                index={index}
                handleCheckboxChange={handleCheckboxChange}
                handleShowToolTip={handleShowToolTip}
                tooltipStates={tooltipStates}
                handleDocumentClick={handleDocumentClick}
                handleNewWindowClick={handleNewWindowClick}
                currency={currency}
                disabled={isDefaultInvoiceOverLimit && !invoice.defaultInvoice}
                maxSelectableBalance={maxSelectableBalance}
                partialPaymentAmount={partialPaymentAmount}
              />
            ))}

            {/* Show All/Show Less Toggle Button */}
            {shouldShowToggleButton && (
              <div className="invoice-row-wrapper data-row">
                <button className="show-all-text" onClick={handleToggleShowAll}>
                  {showAllInvoices ? (
                    <FormattedMessage id="BATCH_SHOW_LESS" defaultMessage="Show less" />
                  ) : (
                    <FormattedMessage id="BATCH_SHOW_ALL" defaultMessage="Show all" />
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <BatchInvoiceMixedPaymentListTable
            batchInvoices={batchInvoices}
            setBatchInvoices={setBatchInvoices}
            tooltipStates={tooltipStates}
            currency={currency}
            handleCheckboxChange={handleCheckboxChange}
            handleShowToolTip={handleShowToolTip}
            handleDocumentClick={handleDocumentClick}
            handleNewWindowClick={handleNewWindowClick}
            maxSelectableBalance={maxSelectableBalance}
            isDefaultInvoiceOverLimit={isDefaultInvoiceOverLimit}
            partialPaymentAmount={partialPaymentAmount}
          />
        )}
      </div>
      <style jsx>{`
        .batch-invoice-list-table {
          margin-bottom: 32px;
        }

        .invoice-table-container {
          background-color: ${colors.white};
          border-radius: 8px;
          border: 1px solid ${colors.gray06};
          overflow: hidden;
        }

        .invoice-row-wrapper {
          display: flex;
          width: 100%;
          height: 40px;
          padding: 12px;
          justify-content: space-between;
          align-items: center;
        }

        .data-row {
          background-color: ${colors.white};
          border-bottom: 1px solid ${colors.gray06};
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .checkbox-column {
          width: 46px;
          display: flex;
          top: 10px;
        }

        .invoice-number-wrapper {
          flex: 1;
          text-align: left;
          margin-right: 10px;
        }

        .section-wrapper {
          flex: 1;
        }

        .due-date-section {
          text-align: left;
        }

        .amount-section {
          text-align: right;
          margin-left: 4px;
        }

        .invoice-data {
          font-family: AvenirNextforINTUIT-Regular;
          font-size: ${fontSize.xs};
          color: ${colors.gray};
        }

        .due-date-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .attention-icon {
          width: 16px;
          height: 16px;
          background-color: ${colors.orangeWarn3};
          border-radius: 50%;
          display: flex;
          justify-content: center;
          font-size: 10px;
          color: white;
          font-weight: bold;
        }

        .actions-column {
          flex: 0 0 40px;
          display: flex;
        }

        .menu-button {
          width: 36px;
          height: 36px;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
        }

        .three-dots {
          width: 3px;
          height: 3px;
          background-color: ${colors.gray02};
          border-radius: 50%;
          position: relative;
        }

        .three-dots::before,
        .three-dots::after {
          content: '';
          width: 3px;
          height: 3px;
          background-color: ${colors.gray02};
          border-radius: 50%;
          position: absolute;
          left: 0;
        }

        .three-dots::before {
          top: -6px;
        }

        .three-dots::after {
          top: 6px;
        }

        .show-all-text {
          font-family: AvenirNextforINTUIT-Demi;
          font-weight: bold;
          font-size: ${fontSize.xxs};
          line-height: 130%;
          letter-spacing: 0%;
          color: ${colors.gray};
          border: none;
          border-radius: 4px;
          width: 85px;
          height: 24px;
          text-align: center;
          background-color: ${colors.gray06};
          cursor: pointer;
        }

        .show-all-text:hover {
          background-color: ${colors.gray05};
        }
      `}</style>
    </>
  );
};

/**
 * Build the current invoice object from store data
 */
function buildCurrentInvoice(sale: any, insight: any, wallet: any): UnpaidInvoice {
  const enabledPaymentMethods = wallet?.enabledPaymentMethods || [];

  return {
    link: buildCurrentPageLink(),
    saleId: sale.id || '',
    docNumber: saleSelectors.referenceNumberSelector(sale),
    balance: saleSelectors.balanceSelector(sale) || 0,
    paymentMethods: buildPaymentMethods(enabledPaymentMethods),
    partiallyPaid: insightSelectors.isPartiallyPaidSelector(insight) || false,
    overDue: saleSelectors.isOverdueSelector(sale) || false,
    dueDate: saleSelectors.dueDateSelector(sale) || '',
    defaultInvoice: true,
    selected: true,
  };
}

// Maximum number of invoices to display in batch payments (including default invoice)
const MAX_BATCH_INVOICES = 100;

function mapStateToProps(store: any): any {
  const { insight, sale, wallet, invoiceDocument, config, featureFlags, companyInfo } = store;
  const batchData = insightSelectors.batchUnpaidInvoices(insight);
  const enabledPaymentMethods = walletSelectors.enabledPaymentMethods(wallet) || [];

  if (!batchData || !batchData.unpaidInvoices) {
    return {
      unpaidInvoices: batchData,
      currency: saleSelectors.currencySelector(sale),
      enabledPaymentMethods,
      insight: insight,
      sale,
      invoiceDocument: invoiceDocument,
      region: companyInfo?.region,
      featureFlags,
      amountConfig: config?.amountConfig,
    };
  }

  // Build current invoice and combine with unpaid invoices
  const currentInvoice = buildCurrentInvoice(sale, insight, wallet);
  const otherInvoices = Array.isArray(batchData.unpaidInvoices)
    ? batchData.unpaidInvoices.filter((inv: any) => {
        // Use docNumber for comparison if available, otherwise use saleId
        const currentId = currentInvoice.docNumber || currentInvoice.saleId;
        const invId = inv.docNumber || inv.saleId;
        return invId !== currentId;
      })
    : [];

  // Limit to maximum 100 invoices total (including default invoice)
  // Always include default invoice first, then take up to 99 others
  const allInvoices = [currentInvoice, ...otherInvoices];
  const originalInvoicesCount = allInvoices.length; // Track original count before limiting
  const limitedInvoices = allInvoices.slice(0, MAX_BATCH_INVOICES);

  return {
    unpaidInvoices: {
      ...batchData,
      unpaidInvoices: limitedInvoices,
      originalInvoicesCount, // Pass original count to distinguish 100 vs 100+
    },
    currency: saleSelectors.currencySelector(sale),
    enabledPaymentMethods,
    insight: insight,
    sale: sale, // Pass sale for customer ID tracking in batch payments analytics
    invoiceDocument: invoiceDocument,
    region: companyInfo?.region,
    featureFlags,
    amountConfig: config?.amountConfig,
  };
}

export default injectIntl(
  // @ts-ignore
  connect(mapStateToProps, {
    fetchPDFDocument,
    downloadPDFDocument,
    showModal: modalActions.show,
  })(BatchInvoiceListTable)
);
